import { ChartData } from '../../../api';
import type { FilterOption } from '../../../api/types';

/**
 * Global in-memory cache for dashboard data (charts + filters).
 *
 * ## Chart data
 * Keyed by `${dashboardId}:${chartId}:${filtersKey}` so that different
 * filter combinations each have their own entry.
 *
 * ## Filter options
 * Keyed by `filter:${dashboardId}:${filterId}`.
 * Filter options are per-dashboard/filter — they don't vary with user
 * filter selections (the options list is static for a given filter definition).
 *
 * ## Lifecycle
 *   - Populated after a successful fetch (useChartData / FilterControl).
 *   - Read on mount — cache hit skips API call (zero flash).
 *   - Written by ChartEditPage on save — return to dashboard is instant.
 *   - Cleared on explicit refresh or page reload (in-memory only).
 *
 * No TTL / auto-expiry — data is valid until:
 *   1. Filters change (different chart cache key)
 *   2. User clicks refresh → `invalidateAll()`
 *   3. Chart saved in edit mode → `set()` with fresh data
 *   4. Hard page reload (Map is garbage-collected)
 */

// ─── Chart cache ─────────────────────────────────────────────────────────────

export interface CacheEntry {
  data: ChartData;
  /** Epoch ms when data was fetched or last updated. */
  fetchedAt: number;
}

// ─── Filter cache ────────────────────────────────────────────────────────────

export interface FilterCacheEntry {
  options: FilterOption[];
  /** Epoch ms when options were fetched. */
  fetchedAt: number;
}

// Single Map for both chart and filter data — prefixed keys keep them separate.
const cache = new Map<string, CacheEntry | FilterCacheEntry>();

function buildKey(dashboardId: string, chartId: string, filtersKey: string): string {
  return `${dashboardId}:${chartId}:${filtersKey}`;
}

/** Retrieve cached chart data for the given dashboard/chart/filters combo. */
export function getCachedChartData(
  dashboardId: string,
  chartId: string,
  filtersKey: string,
): CacheEntry | null {
  return (cache.get(buildKey(dashboardId, chartId, filtersKey)) as CacheEntry | undefined) ?? null;
}

/** Retrieve any cached chart data for the given dashboard/chart (ignores filtersKey). */
export function getAnyCachedChartData(
  dashboardId: string,
  chartId: string,
): CacheEntry | null {
  const prefix = `${dashboardId}:${chartId}:`;
  for (const [key, entry] of cache.entries()) {
    if (key.startsWith(prefix) && !key.startsWith('filter:')) {
      return entry as CacheEntry;
    }
  }
  return null;
}

/** Store chart data in cache with current timestamp. */
export function setCachedChartData(
  dashboardId: string,
  chartId: string,
  filtersKey: string,
  data: ChartData,
): void {
  cache.set(buildKey(dashboardId, chartId, filtersKey), {
    data,
    fetchedAt: Date.now(),
  });
}

/** Invalidate a single chart (all filter variants) or a specific one. */
export function invalidateChart(dashboardId: string, chartId: string, filtersKey?: string): void {
  if (filtersKey !== undefined) {
    cache.delete(buildKey(dashboardId, chartId, filtersKey));
    return;
  }
  const prefix = `${dashboardId}:${chartId}:`;
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
}

/** Invalidate all cached data for a dashboard (charts + filters). */
export function invalidateDashboard(dashboardId: string): void {
  const chartPrefix = `${dashboardId}:`;
  const filterPrefix = `filter:${dashboardId}:`;
  for (const key of cache.keys()) {
    if (key.startsWith(chartPrefix) || key.startsWith(filterPrefix)) {
      cache.delete(key);
    }
  }
}

/** Clear the entire cache (used by future refresh button). */
export function invalidateAll(): void {
  cache.clear();
}

// ─── Filter option cache ─────────────────────────────────────────────────────

function buildFilterKey(dashboardId: string, filterId: string): string {
  return `filter:${dashboardId}:${filterId}`;
}

/** Retrieve cached filter options for the given dashboard/filter combo. */
export function getCachedFilterData(
  dashboardId: string,
  filterId: string,
): FilterCacheEntry | null {
  return (cache.get(buildFilterKey(dashboardId, filterId)) as FilterCacheEntry) ?? null;
}

/** Store filter options in cache with current timestamp. */
export function setCachedFilterData(
  dashboardId: string,
  filterId: string,
  options: FilterOption[],
): void {
  cache.set(buildFilterKey(dashboardId, filterId), {
    options,
    fetchedAt: Date.now(),
  });
}
